import { useState } from 'react'
import { GeneralOperation } from '~/hooks/toast/eventHandlers/generalToastEventHandler'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { isElectron } from '~/utils/common'

const webAppUrl = 'https://app.mangovoice.com'
type ReplaceType = 'web' | 'desktop'

export const doReplace = (value: string, type: ReplaceType = 'web') => {
  return type === 'desktop'
    ? value.replace('mangofiles', 'mango').replace('bundle/', '')
    : value.replace('mangofiles://', webAppUrl).replace('bundle/', '/')
}

export function useClipboard(
  args: {
    timeout?: number
    onSuccess?: () => void
    onFailure?: (reason?: string) => void
  } = {},
) {
  const { emit } = useEmitterService()

  const handleSuccess = () => {
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.GENERAL,
      operation: GeneralOperation.GeneralCopyToClipboard,
    })
  }

  const handleFailure = (reason?: any) => {
    console.error(reason)
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Error,
      domain: NotificationDomain.GENERAL,
      operation: GeneralOperation.GeneralCopyToClipboard,
    })
  }

  const {
    timeout = 2000,
    onSuccess = handleSuccess,
    onFailure = handleFailure,
  } = args
  const [error, setError] = useState<Error | null>(null)
  const [hasCopied, setHasCopied] = useState(false)
  const [copyTimeout, setCopyTimeout] = useState<number | null>(null)

  const handleCopyResult = (value: boolean) => {
    window.clearTimeout(copyTimeout!)
    setCopyTimeout(window.setTimeout(() => setHasCopied(false), timeout))
    setHasCopied(value)
  }

  const copyToClipboard = (
    valueToCopy: string,
    onSuccessOverride?: () => void,
    onFailureOverride?: (reason?: string) => void,
  ) => {
    if ('clipboard' in navigator) {
      navigator.clipboard
        .writeText(valueToCopy ?? '')
        .then(() => {
          handleCopyResult(true)
          if (onSuccessOverride) {
            onSuccessOverride()
          } else {
            onSuccess()
          }
        })
        .catch((reason: string) => {
          if (isElectron) {
            try {
              window.electron?.clipboard?.writeText(
                doReplace(valueToCopy, 'web') ?? '',
              )
              handleCopyResult(true)

              if (onSuccessOverride) {
                onSuccessOverride()
              } else {
                onSuccess()
              }
            } catch (error: any) {
              setError(error)

              if (onFailureOverride) {
                onFailureOverride()
              } else {
                onFailure(reason || 'Failed to copy to clipboard')
              }
            }
          } else {
            if (onFailureOverride) {
              onFailureOverride()
            } else {
              onFailure(reason || 'Failed to copy to clipboard')
            }
          }
        })
    } else {
      setError(new Error('Clipboard not supported on this browser'))

      if (onFailureOverride) {
        onFailureOverride()
      } else {
        onFailure('Clipboard not supported this browser')
      }
    }
  }

  const doReset = () => {
    setHasCopied(false)
    setError(null)
    window.clearTimeout(copyTimeout!)
  }

  return { copyToClipboard, doReset, error, hasCopied }
}
